import { useCallback, useEffect, useMemo, useState } from 'react';
import logAbortableError from '../logAbortableError';

const leavesOf = (items) =>
    items.flatMap((item) => {
        if (Array.isArray(item?.children)) return item.children;
        if (Array.isArray(item?.options)) return item.options;
        return [item];
    });

/** Return the string key of an option in either vocabulary (`{value,label}` or select2 `{id,text}`), or null. */
export const optionKey = (item) => {
    if (item == null) return null;
    if (typeof item !== 'object') return String(item);
    const id = item.value !== undefined ? item.value : item.id;
    return id == null ? null : String(id);
};

/** Return the option list of a select2 `{ results: [...] }` payload or a bare array; anything else is empty. */
export const toRemoteOptions = (payload) => {
    const list = Array.isArray(payload) ? payload : payload?.results;
    return Array.isArray(list) ? list : [];
};

/** Build `url?term=…` (or `&term=…` when the url already has a query). */
export const remoteSearchUrl = (url, term) =>
    `${url}${url.includes('?') ? '&' : '?'}term=${encodeURIComponent(String(term ?? '').trim())}`;

/** Append the `extras` whose key is not already among `primary` (group children included), keeping order. */
export const mergeRemoteOptions = (primary, extras) => {
    const seen = new Set(leavesOf(primary).map(optionKey));
    const appended = extras.filter((item) => {
        const key = optionKey(item);
        if (key == null || seen.has(key)) return false;
        seen.add(key);
        return true;
    });
    return appended.length ? [...primary, ...appended] : primary;
};

const keepPicked = (previous, selectedKeys, results) => {
    const resultsByKey = new Map(leavesOf(results).map((item) => [optionKey(item), item]));
    const next = new Map();
    selectedKeys.forEach((key) => {
        const option = resultsByKey.get(key) ?? previous.get(key);
        if (option) next.set(key, option);
    });
    const unchanged = next.size === previous.size && [...next].every(([key, option]) => previous.get(key) === option);
    return unchanged ? previous : next;
};

/**
 * Feed core/SelectDropdown from a select2-style search source: `GET url?term=…` answering
 * `{ results: [{id, text}] }` or a bare option array. Spread the result into the select as
 * `options`, `loading`, `loadFailed={failed}` and `onSearch`. Only the latest term's response is
 * applied; selected values keep their labels after later searches replace the results.
 *
 * @param {string} [url] search endpoint; without one, `options` is just `knownOptions`
 * @param {object} [config]
 * @param {number} [config.minSearchLength=0] shortest term searched; 0 also loads the unfiltered list on mount
 * @param {*} [config.selected] selected value or values
 * @param {Array} [config.knownOptions] options with already-known labels (saved selection, static entries), appended after the results
 * @param {number} [config.debounceMs=0] delay before requesting a term; SelectDropdown already debounces `onSearch`
 * @returns {{ options: Array, loading: boolean, failed: boolean, onSearch: (term: string) => void }}
 */
const useRemoteOptions = (url, { minSearchLength = 0, selected = [], knownOptions = [], debounceMs = 0 } = {}) => {
    const [term, setTerm] = useState('');
    const [remote, setRemote] = useState({ results: [], loading: false, failed: false });
    const [picked, setPicked] = useState(() => new Map());

    const selectedSignature = JSON.stringify(
        (Array.isArray(selected) ? selected : [selected]).map(optionKey).filter((key) => key != null),
    );

    const searchable = Boolean(url) && term.trim().length >= minSearchLength;

    useEffect(() => {
        if (!searchable) return undefined;
        let stale = false;
        const controller = typeof AbortController === 'undefined' ? null : new AbortController();
        const timer = setTimeout(async () => {
            setRemote((previous) => ({ ...previous, loading: true, failed: false }));
            try {
                const response = await fetch(remoteSearchUrl(url, term), {
                    headers: { Accept: 'application/json' },
                    signal: controller?.signal,
                });
                if (!response.ok) throw new Error(`${response.status} ${response.statusText}`);
                const payload = await response.json();
                if (!stale) setRemote({ results: toRemoteOptions(payload), loading: false, failed: false });
            } catch (error) {
                if (stale) return;
                logAbortableError(`useRemoteOptions: option source ${url} failed`, error);
                setRemote({ results: [], loading: false, failed: true });
            }
        }, debounceMs);
        return () => {
            stale = true;
            clearTimeout(timer);
            controller?.abort();
        };
    }, [url, term, searchable, debounceMs]);

    useEffect(() => {
        const selectedKeys = JSON.parse(selectedSignature);
        setPicked((previous) => keepPicked(previous, selectedKeys, remote.results));
    }, [selectedSignature, remote.results]);

    const options = useMemo(() => {
        const selectedKeys = JSON.parse(selectedSignature);
        const pickedOptions = selectedKeys.map((key) => picked.get(key)).filter(Boolean);
        return mergeRemoteOptions(url ? remote.results : [], [...pickedOptions, ...knownOptions]);
    }, [url, remote.results, picked, selectedSignature, knownOptions]);

    const onSearch = useCallback((nextTerm) => setTerm(String(nextTerm ?? '')), []);

    return { options, loading: Boolean(url) && remote.loading, failed: Boolean(url) && remote.failed, onSearch };
};

export default useRemoteOptions;
